// ClubEventDetailPage — Feb 2026 (Club Events V2).
//
// Rippers-style premium event landing page:
//   • Flyer-first hero with phase pill (Upcoming / Happening Now / Past)
//   • Category badge (Social / Tennis / Charity / Club)
//   • Date/time/location chips
//   • Highlight callout when present
//   • Optional RSVP card ("I'm In" / "Can't make it") + attendee count
//   • "Who's In?" avatars + names (initials only; no contact info)
//   • Polished "About This Event" section
//   • External CTA button when configured
//
// Everything RSVP-related goes through authenticated API endpoints —
// no direct Supabase access from this file.
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CaretLeft, CalendarBlank, MapPin, ArrowSquareOut, Gear, Users,
  Check, X as XIcon, Star, CurrencyDollar, HandHeart, CreditCard, Warning,
} from '@phosphor-icons/react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { clubEventsAPI } from '../lib/clubEventsApi';
import { paymentsAPI } from '../lib/paymentsApi';
import RegistrationCTA from '../components/events/RegistrationCTA';
import PostRegistrationPaymentPrompt from '../components/events/PostRegistrationPaymentPrompt';
import EventPhotoGallery from '../components/events/EventPhotoGallery';

// Stripe.js is a singleton — load once and reuse across every mount.
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const CATEGORY_LABEL = {
  social:  'Social',
  tennis:  'Tennis',
  charity: 'Charity',
  club:    'Club',
};

const PAYMENT_METHOD_LABEL = {
  stripe: 'Stripe',
  venmo:  'Venmo',
  zelle:  'Zelle',
  cash:   'Cash',
};

function formatFee(cents) {
  if (cents == null) return null;
  if (cents === 0) return 'Free';
  const d = cents / 100;
  return d % 1 === 0 ? `$${d.toFixed(0)}` : `$${d.toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return null; }
}
function fmtTime(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); }
  catch { return null; }
}

// Time-based phase — matches EventsPage bucketing so /events and detail
// agree on Upcoming / Happening Now / Past.
function phaseOf(ev) {
  const now = Date.now();
  const s = ev.starts_at ? Date.parse(ev.starts_at) : NaN;
  const e = ev.ends_at   ? Date.parse(ev.ends_at)   : NaN;
  if (!Number.isFinite(s)) return 'past';
  if (s > now) return 'upcoming';
  if (Number.isFinite(e)) return e >= now ? 'in_progress' : 'past';
  return 'past';
}

const PHASE_STYLE = {
  upcoming:    { text: 'Upcoming',      cls: 'bg-[#c9a93a] text-[#3a2c05]' },
  in_progress: { text: 'Happening now', cls: 'bg-[#015e3f] text-white' },
  past:        { text: 'Past',          cls: 'bg-[#3f463f] text-white/85' },
};

// --------------------------------------------------------------------------
// Club Event registration CTA — thin wrapper around the shared
// `RegistrationCTA` component so Club Events use the exact same
// member-facing wording as Tournaments and Round Robins:
//
//   Register for Event  →  You are registered for this event · Withdraw
//
// The underlying RSVP table/model is unchanged; this is a UX consistency
// pass, not a data-model rename. `viewer_paid` flows through to enable the
// paid-safety warning in the Withdraw dialog when applicable.
function ClubEventRegistrationCTA({ ev, user, onChange, onRegistered }) {
  const [busy, setBusy] = useState(false);
  const going = !!ev.viewer_rsvp;

  const handleRegister = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await clubEventsAPI.rsvp(ev.id);
      toast.success('Registered.');
      onRegistered?.();
      onChange?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not register.');
    } finally { setBusy(false); }
  };

  const handleWithdraw = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await clubEventsAPI.unrsvp(ev.id);
      toast.success('Withdrawn from event.');
      onChange?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not withdraw.');
    } finally { setBusy(false); }
  };

  return (
    <RegistrationCTA
      status="registration_open"         /* Club events don't have a
                                            multi-phase registration state;
                                            emulate always-open. */
      isRegistered={going}
      isFull={false}
      onOpenRegistration={handleRegister}
      onWithdraw={handleWithdraw}
      withdrawBusy={busy}
      eventKind="club_event"
      eventId={ev.id}
      onReload={onChange}
      viewerPaid={!!ev.viewer_paid}
    />
  );
}


// --------------------------------------------------------------------------
// Attendee running roster — clean numbered list similar to Tournament/RR
// registration rosters. When the event has payment enabled, each row also
// carries a Paid / Payment due chip. Name-privacy rules (first name + last
// initial only; approved/active members only) are enforced server-side.
function AttendeeRoster({ clubEventId, refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    clubEventsAPI.listRsvps(clubEventId)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ attendees: [], attendee_count: 0, payment_enabled: false }); });
    return () => { alive = false; };
  }, [clubEventId, refreshKey]);

  if (!data) return (
    <div className="text-[12px] text-[#6a766e]" data-testid="ce-roster-loading">Loading attendees…</div>
  );
  const paymentEnabled = !!data.payment_enabled;
  if (data.attendee_count === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#e3dcd1] bg-[#faf7f0] px-4 py-5 text-center" data-testid="ce-roster-empty">
        <div className="text-sm font-medium text-[#013523]">Be the first to say you&rsquo;re in.</div>
        <div className="text-[12px] text-[#6a766e] mt-1">Nobody has RSVP&rsquo;d yet. Tap the button above.</div>
      </div>
    );
  }

  return (
    <ol className="divide-y divide-[#f0ece3] rounded-xl border border-[#e3dcd1] bg-white overflow-hidden" data-testid="ce-roster-list">
      {data.attendees.map((a, i) => (
        <li key={a.profile_id}
            className="flex items-center gap-3 px-3.5 py-2.5"
            data-testid={`ce-roster-row-${a.profile_id}`}>
          <span className="w-6 shrink-0 text-right text-[12px] tabular-nums text-[#6a766e]">
            {i + 1}.
          </span>
          <span className="flex-1 min-w-0 text-[13.5px] text-[#013523] truncate">
            {a.first_name}{a.last_initial ? ` ${a.last_initial}` : ''}
          </span>
          {paymentEnabled && (
            a.paid ? (
              <span
                data-testid={`ce-roster-paid-${a.profile_id}`}
                className="shrink-0 inline-flex items-center gap-1 text-[11.5px] font-medium text-[#015e3f] bg-[#e6f2ec] border border-[#015e3f]/25 rounded-full px-2 py-0.5"
              >
                <Check size={11} weight="bold" /> Paid
              </span>
            ) : (
              <span
                data-testid={`ce-roster-unpaid-${a.profile_id}`}
                className="shrink-0 inline-flex items-center gap-1 text-[11.5px] font-medium text-[#7c5b1a] bg-[#f5eed8] border border-[#c9a93a]/40 rounded-full px-2 py-0.5"
              >
                Payment due
              </span>
            )
          )}
        </li>
      ))}
    </ol>
  );
}

// --------------------------------------------------------------------------
// Payment card — polished, category-aware ("Donate" for charity, "Pay Now"
// otherwise). Behavior per payment_type:
//   • stripe   → Embedded Stripe Checkout in-page. Registration must exist.
//   • venmo/zelle → external link button (existing behavior).
//   • cash    → informational note, no link, no button.
// Amount is hidden when null (open-ended donations).
function PaymentCard({ ev, viewer, onPaid, autoStartToken = 0 }) {
  const [busy, setBusy] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // 'idle' | 'polling' | 'awaiting_confirmation' — drives the recovery card
  // that replaces the embedded form when confirmation takes too long.
  const [confirmPhase, setConfirmPhase] = useState('idle');
  const [retrying, setRetrying] = useState(false);
  const pollTimer = useRef(null);
  const pollStopped = useRef(false);

  useEffect(() => () => {
    pollStopped.current = true;
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  const fee = formatFee(ev.entry_fee_cents);
  const method = ev.payment_type ? PAYMENT_METHOD_LABEL[ev.payment_type] : null;
  const link = ev.payment_link || null;
  const isCharity = ev.category === 'charity';
  const isStripe = ev.payment_type === 'stripe';
  const isCash = ev.payment_type === 'cash';
  const isPaid = !!ev.viewer_paid;
  const showExternalButton = !!link && !isCash && !isStripe;
  const buttonLabel = isCharity ? 'Donate' : 'Pay Now';

  const showAmountLine = fee && fee !== 'Free';
  const amountPrefix = isCharity ? 'Suggested contribution:' : 'Fee:';

  // Stripe embedded flow ---------------------------------------------------
  const startStripeCheckout = async () => {
    if (busy) return;
    if (!stripePromise) {
      toast.error('Payments are not configured. Try again shortly.');
      return;
    }
    if (!viewer) {
      toast.error('Sign in to pay for this event.');
      return;
    }
    if (!ev.viewer_rsvp) {
      toast.error("You need to RSVP first — tap \u201cI\u2019m in\u201d before paying.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await paymentsAPI.createCheckout({
        eventKind: 'club_event',
        eventId: ev.id,
      });
      setClientSecret(data.client_secret);
      setSessionId(data.session_id);
      setConfirmPhase('idle');
    } catch (e) {
      const d = e.response?.data?.detail;
      const code = typeof d === 'object' ? d?.code : null;
      const msg = typeof d === 'string' ? d : d?.message || 'Could not start payment.';
      if (code === 'registration_required') {
        toast.error("You need to RSVP first — tap \u201cI\u2019m in\u201d before paying.");
      } else if (code === 'already_paid') {
        toast.success("You\u2019re already marked paid.");
        onPaid?.();
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  // Shared polling helper — used both by handleEmbeddedComplete (first
  // pass, after Stripe's onComplete fires) and by Retry Status (recovery
  // pass). NEVER creates a new Checkout Session.
  const pollUntilPaid = useCallback((sid, { softTimeoutMs = 30000 } = {}) => {
    if (!sid) return;
    pollStopped.current = false;
    const start = Date.now();
    const tick = async () => {
      if (pollStopped.current) return;
      try {
        const { data } = await paymentsAPI.getSession(sid);
        if (data.status === 'paid') {
          pollStopped.current = true;
          setClientSecret(null);
          setSessionId(null);
          setConfirming(false);
          setPolling(false);
          setConfirmPhase('idle');
          toast.success('Payment received. Thanks!');
          onPaid?.();
          return;
        }
      } catch { /* transient — keep polling */ }
      if (Date.now() - start > softTimeoutMs) {
        // Soft timeout: stop the automatic polling, unmount the Stripe
        // iframe (we're done with it), and offer a manual Retry.
        pollStopped.current = true;
        setClientSecret(null);
        setPolling(false);
        setConfirming(false);
        setConfirmPhase('awaiting_confirmation');
        return;
      }
      pollTimer.current = setTimeout(tick, 1500);
    };
    setPolling(true);
    tick();
  }, [onPaid]);

  // Called by Stripe when the embedded form completes. Webhook remains
  // authoritative — this callback is a UI cue only, never marks anyone
  // paid client-side. Our GET /payments/session also self-heals from
  // Stripe if the webhook is lost/late.
  const handleEmbeddedComplete = useCallback(() => {
    setConfirming(true);
    setConfirmPhase('polling');
    pollUntilPaid(sessionId);
  }, [sessionId, pollUntilPaid]);

  // Retry Status: only re-checks confirmation via our GET endpoint (which
  // self-heals from Stripe when the webhook is lost). NEVER calls
  // createCheckout / mints a new Session / charges the member again.
  const retryConfirmation = useCallback(async () => {
    if (!sessionId || retrying) return;
    setRetrying(true);
    try {
      const { data } = await paymentsAPI.getSession(sessionId);
      if (data.status === 'paid') {
        setSessionId(null);
        setConfirmPhase('idle');
        toast.success('Payment received. Thanks!');
        onPaid?.();
      } else {
        // Not confirmed yet — kick off another short poll window.
        setConfirmPhase('polling');
        pollUntilPaid(sessionId, { softTimeoutMs: 15000 });
      }
    } catch {
      toast.error("Couldn\u2019t reach Stripe just now. Try again in a moment.");
    } finally { setRetrying(false); }
  }, [sessionId, retrying, onPaid, pollUntilPaid]);


  const cancelEmbedded = () => {
    setClientSecret(null);
    setSessionId(null);
  };

  // Auto-start on token bump — driven by the shared post-registration
  // payment prompt "Pay Now" action. Guarded so a double bump / re-render
  // never mints a second session.
  const lastAutoStartToken = useRef(0);
  useEffect(() => {
    if (!autoStartToken) return;
    if (autoStartToken === lastAutoStartToken.current) return;
    lastAutoStartToken.current = autoStartToken;
    if (!isStripe) return;
    if (clientSecret || confirmPhase !== 'idle') return;
    if (!viewer || !ev.viewer_rsvp || !stripePromise) return;
    startStripeCheckout();
  }, [autoStartToken]);

  // -----------------------------------------------------------------------
  // Terminal "paid" state — replaces the button entirely.
  if (isPaid) {
    return (
      <div
        id="event-payment-section"
        data-testid="ce-payment-card"
        className="relative rounded-3xl overflow-hidden bg-white border border-[#015e3f]/40 p-5 sm:p-6 shadow-sm"
      >
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#015e3f] to-[#013523]" />
        <div className="pl-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#015e3f] font-semibold flex items-center gap-1.5">
              <Check size={12} weight="bold" /> Payment received
            </div>
            {showAmountLine && (
              <div className="mt-1 flex items-baseline gap-2" data-testid="ce-payment-paid-amount">
                <span className="text-3xl font-serif font-semibold text-[#013523]">{fee}</span>
                <span className="text-[13px] text-[#015e3f] font-medium">Paid</span>
              </div>
            )}
          </div>
          <span className="shrink-0 inline-flex items-center gap-1 text-[12px] font-semibold text-[#015e3f] bg-[#e6f2ec] border border-[#015e3f]/30 rounded-full px-3 py-1.5"
                data-testid="ce-payment-paid-chip">
            <Check size={12} weight="bold" /> Paid
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      id="event-payment-section"
      data-testid="ce-payment-card"
      className="relative rounded-3xl overflow-hidden bg-white border border-[#c9a93a]/30 p-5 sm:p-6 shadow-sm"
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#c9a93a] to-[#a88824]" />
      <div className="pl-2">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#7c5b1a] font-semibold flex items-center gap-1.5">
              {isCharity ? <HandHeart size={12} weight="fill" /> : <CurrencyDollar size={12} weight="fill" />}
              {isCharity ? 'Contribution' : 'Payment'}
            </div>
            {showAmountLine && (
              <div className="mt-1 flex items-baseline gap-2" data-testid="ce-payment-amount">
                <span className="text-[12.5px] text-[#6a766e]">{amountPrefix}</span>
                <span className="text-3xl font-serif font-semibold text-[#013523]">{fee}</span>
              </div>
            )}
            {method && (
              <div className="mt-1.5 text-[12.5px] text-[#3a4038]" data-testid="ce-payment-method">
                <span className="text-[#6a766e]">Method:</span>{' '}
                <span className="font-medium text-[#013523]">{method}</span>
              </div>
            )}
            {isCharity && !showAmountLine && (
              <div className="mt-1 text-[13px] text-[#3a4038] italic" data-testid="ce-payment-open">
                Any amount helps.
              </div>
            )}
          </div>
          {isStripe && !clientSecret && (
            <button
              type="button"
              onClick={startStripeCheckout}
              disabled={busy || !viewer || !ev.viewer_rsvp}
              data-testid="ce-payment-btn"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full min-h-[44px] px-5 py-2 text-sm font-semibold bg-[#013523] text-white hover:bg-[#024029] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <CreditCard size={14} weight="bold" />
              {busy ? 'Opening…' : buttonLabel}
            </button>
          )}
          {showExternalButton && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ce-payment-btn"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full min-h-[44px] px-5 py-2 text-sm font-semibold bg-[#013523] text-white hover:bg-[#024029] transition-colors"
            >
              {isCharity ? <HandHeart size={14} weight="bold" /> : <CurrencyDollar size={14} weight="bold" />}
              {buttonLabel} <ArrowSquareOut size={12} />
            </a>
          )}
        </div>
        {isCash && (
          <div className="mt-2 text-[11.5px] text-[#6a766e]" data-testid="ce-payment-cash-hint">
            Bring cash to the event.
          </div>
        )}
        {isStripe && viewer && !ev.viewer_rsvp && !clientSecret && (
          <div className="mt-2 flex items-start gap-1.5 text-[12px] text-[#7c5b1a]"
               data-testid="ce-payment-need-rsvp">
            <Warning size={13} weight="fill" className="mt-0.5 shrink-0" />
            <span>RSVP first, then you can pay.</span>
          </div>
        )}
        {isStripe && !viewer && (
          <div className="mt-2 text-[12px] text-[#6a766e]" data-testid="ce-payment-signin-hint">
            Sign in to pay for this event.
          </div>
        )}

        {/* Embedded checkout container. Rendered only after we have a
            client_secret; unmounts on complete/cancel. */}
        {clientSecret && stripePromise && (
          <div className="mt-4 rounded-2xl border border-[#e3dcd1] bg-[#faf7f0] p-2"
               data-testid="ce-payment-embedded">
            {confirming && (
              <div className="text-center py-2 text-[12px] text-[#013523] font-medium"
                   data-testid="ce-payment-confirming">
                Confirming payment…
              </div>
            )}
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ clientSecret, onComplete: handleEmbeddedComplete }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
            {!polling && (
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={cancelEmbedded}
                  data-testid="ce-payment-cancel-embedded"
                  className="text-[12px] text-[#6a766e] hover:text-[#013523] underline"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Recovery card: shown when Stripe confirmed the payment but our
            webhook confirmation hasn't landed in time. Never triggers a
            new charge — only re-polls our GET endpoint which itself
            self-heals against Stripe. */}
        {confirmPhase === 'awaiting_confirmation' && sessionId && (
          <div
            data-testid="ce-payment-awaiting"
            className="mt-4 rounded-2xl border border-[#c9a93a]/40 bg-[#fff9e6]/70 px-4 py-3.5 space-y-2"
          >
            <div className="flex items-start gap-2">
              <Warning size={16} weight="fill" className="text-[#7c5b1a] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[#3a2c05]">
                  Payment submitted — we&rsquo;re confirming it with Stripe.
                </div>
                <div className="text-[12px] text-[#5a4b1e] mt-0.5">
                  Your card was charged successfully. This can take a few extra seconds. Your registration stays intact either way.
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={retryConfirmation}
                disabled={retrying}
                data-testid="ce-payment-retry-status"
                className="inline-flex items-center gap-1.5 rounded-full min-h-[40px] px-4 py-1.5 text-[13px] font-semibold bg-[#013523] text-white hover:bg-[#024029] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {retrying ? 'Checking…' : 'Retry Status'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
export default function ClubEventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, managedEvents } = useAuth();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState(null);
  const [rsvpTick, setRsvpTick] = useState(0);
  // Post-registration payment prompt orchestration.
  const [payPromptOpen, setPayPromptOpen] = useState(false);
  const [autoStartToken, setAutoStartToken] = useState(0);
  const justRegisteredRef = useRef(false);

  const load = useCallback(() => {
    clubEventsAPI.get(id)
      .then((r) => setEv(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Load failed'));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const phase = useMemo(() => (ev ? phaseOf(ev) : 'upcoming'), [ev]);

  // Fire the post-registration prompt exactly once per successful RSVP
  // action, and only after the reload confirms viewer_rsvp is true.
  const paymentEnabled = !!ev?.payment_enabled;
  const isRsvpd = !!ev?.viewer_rsvp;
  const viewerPaid = !!ev?.viewer_paid;
  const feeCents = ev?.entry_fee_cents;
  const hasChargeableAmount = (feeCents || 0) > 0;
  useEffect(() => {
    if (!ev) return;
    if (!justRegisteredRef.current) return;
    if (!isRsvpd) return;
    justRegisteredRef.current = false;
    if (!paymentEnabled || viewerPaid) return;
    // Require a non-zero fee to surface the prompt across all methods —
    // "You're registered! This Club Event has an entry fee of $0" is
    // never meaningful.
    if (!hasChargeableAmount) return;
    setPayPromptOpen(true);
  }, [ev, isRsvpd, paymentEnabled, viewerPaid, hasChargeableAmount]);

  const handlePayNow = () => {
    setPayPromptOpen(false);
    // Venmo/Zelle open in a new tab; Stripe scrolls to & auto-opens
    // the embedded iframe on the same page. Cash never gets here.
    if (ev?.payment_type === 'venmo' || ev?.payment_type === 'zelle') {
      if (ev.payment_link) window.open(ev.payment_link, '_blank', 'noopener,noreferrer');
      return;
    }
    requestAnimationFrame(() => {
      const el = document.getElementById('event-payment-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setAutoStartToken((n) => n + 1);
    });
  };

  const handlePayLater = () => {
    setPayPromptOpen(false);
  };

  if (err) return (
    <div className="text-sm text-red-700" data-testid="ce-load-error">
      {typeof err === 'string' ? err : (err?.message || 'Load failed')}
    </div>
  );
  if (!ev) return <div className="text-sm text-[#6a766e]" data-testid="ce-loading">Loading…</div>;
  if (ev.status === 'archived' && user?.role !== 'admin') return <Navigate to="/events" replace />;

  const isAdmin = user?.role === 'admin';
  const isEventManager = Array.isArray(managedEvents) && managedEvents.some(
    (m) => m.event_kind === 'club_event' && m.event_id === id,
  );
  const canManage = isAdmin || isEventManager;
  const startDate = fmtDate(ev.starts_at);
  const startTime = fmtTime(ev.starts_at);
  const endTime = ev.ends_at ? fmtTime(ev.ends_at) : null;
  const showCta = ev.cta_label && ev.cta_url;
  const categoryLabel = ev.category ? CATEGORY_LABEL[ev.category] : null;
  const phaseStyle = PHASE_STYLE[phase];

  return (
    <div className="space-y-5" data-testid="club-event-detail">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-[12px] text-[#013523] hover:text-[#015e3f]"
          data-testid="ce-back">
          <CaretLeft size={12} /> Back
        </button>
        {canManage && (
          <Link
            to={`/admin/club-events/${id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#e3dcd1] text-[12px] text-[#013523] hover:border-[#015e3f]/50"
            data-testid="ce-admin-edit"
          ><Gear size={12} /> {isAdmin ? 'Edit' : 'Manage'}</Link>
        )}
      </div>

      {/* Hero — flyer-first, or elegant text-only fallback */}
      <div
        className="relative rounded-[28px] overflow-hidden border border-[#013523]/15 shadow-[0_2px_18px_rgba(1,53,35,0.12)]"
        data-testid="ce-hero"
        style={{ minHeight: 220 }}
      >
        {ev.flyer_url ? (
          <img
            src={ev.flyer_url}
            alt=""
            className="w-full h-auto max-h-[560px] object-cover"
            data-testid="ce-flyer"
          />
        ) : (
          <div className="w-full aspect-[16/9] bg-gradient-to-br from-[#013523] via-[#024029] to-[#015e3f]" data-testid="ce-hero-fallback">
            <div className="w-full h-full flex items-center justify-center opacity-25">
              <Star size={72} weight="duotone" className="text-white" />
            </div>
          </div>
        )}
        {/* Readability gradient over the flyer */}
        <div className="absolute inset-x-0 bottom-0 top-1/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

        {/* Top-left pills: Club Event kind + Category */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap max-w-[80%]">
          <span
            data-testid="ce-kind-pill"
            className="text-[10px] uppercase tracking-widest bg-white/95 text-[#013523] rounded-full px-2 py-0.5 font-semibold"
          >
            Club Event
          </span>
          {categoryLabel && (
            <span
              data-testid="ce-category-pill"
              className="text-[10px] uppercase tracking-widest bg-[#f0e6cf] text-[#7c5b1a] border border-[#c9a93a]/40 rounded-full px-2 py-0.5 font-semibold"
            >
              {categoryLabel}
            </span>
          )}
        </div>

        {/* Top-right pill: phase */}
        <div className="absolute top-3 right-3">
          <span
            data-testid="ce-phase-pill"
            className={`text-[10px] uppercase tracking-widest rounded-full px-2.5 py-0.5 font-semibold ${phaseStyle.cls}`}
          >
            {phaseStyle.text}
          </span>
        </div>

        {/* Bottom overlay: title + attendee count (when applicable) */}
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 text-white">
          <h1 className="text-2xl sm:text-4xl font-serif font-semibold tracking-tight leading-tight drop-shadow" data-testid="ce-title">
            {ev.name}
          </h1>
          {ev.rsvp_enabled && (ev.attendee_count || 0) > 0 && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 text-[12.5px] text-white/90" data-testid="ce-hero-going">
              <Users size={13} weight="bold" /> {ev.attendee_count} registered
            </div>
          )}
        </div>
      </div>

      {ev.status === 'archived' && (
        <div className="rounded-xl bg-[#f3f1ec] border border-[#e3dcd1] px-3 py-2 text-[12px] text-[#6a766e]" data-testid="ce-archived-banner">
          Archived — hidden from Events discovery. Restore from the admin editor to bring it back.
        </div>
      )}

      {/* Chip strip: date, time, location */}
      <div className="flex flex-wrap gap-2" data-testid="ce-chips">
        {startDate && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#e3dcd1] px-3 py-1.5 text-[12.5px] text-[#013523]" data-testid="ce-chip-when">
            <CalendarBlank size={13} className="text-[#6a766e]" />
            <span>{startDate}{startTime ? ` · ${startTime}` : ''}{endTime ? ` \u2013 ${endTime}` : ''}</span>
          </div>
        )}
        {ev.location_text && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#e3dcd1] px-3 py-1.5 text-[12.5px] text-[#013523]" data-testid="ce-chip-where">
            <MapPin size={13} className="text-[#6a766e]" />
            <span>{ev.location_text}</span>
          </div>
        )}
      </div>

      {/* Additional event photos (carousel) — flyer already shown in hero. */}
      <EventPhotoGallery flyerUrl={null} eventName={ev.name} kind="club_event" eventId={ev.id} />

      {/* Highlight callout */}
      {ev.highlight_text && (
        <div
          data-testid="ce-highlight"
          className="relative rounded-2xl border border-[#c9a93a]/40 bg-[#fff9e6]/60 backdrop-blur-[2px] px-4 py-3.5 text-[14px] text-[#3a2c05] leading-relaxed"
        >
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#c9a93a] rounded-l-2xl" />
          <div className="pl-2 italic font-medium">{ev.highlight_text}</div>
        </div>
      )}

      {/* Payment card — independent from RSVP. Only rendered when the
          admin has enabled payment on this event. Renders BELOW the
          attendee roster (spec: details → RSVP → roster → payment). */}

      {/* Registration CTA — shared across Tournaments, Round Robins, and
          Club Events. Wording is intentionally identical so members
          learn one registration pattern:
             Register for Event → You are registered for this event · Withdraw
          Payment status ("Payment Due" / "✓ Paid") flows separately via
          the Payment card below. */}
      {ev.rsvp_enabled && user && phase !== 'past' && (
        <ClubEventRegistrationCTA
          ev={ev}
          user={user}
          onChange={() => { load(); setRsvpTick((n) => n + 1); }}
          onRegistered={() => { justRegisteredRef.current = true; }}
        />
      )}
      {ev.rsvp_enabled && user && phase === 'past' && (
        <div
          data-testid="ce-rsvp-past"
          className="rounded-2xl border border-[#e3dcd1] bg-white px-4 py-3 text-[13px] text-[#6a766e]"
        >
          This event has ended. Registration is closed.
        </div>
      )}
      {ev.rsvp_enabled && !user && (
        <div
          data-testid="ce-rsvp-signin-hint"
          className="rounded-2xl border border-[#e3dcd1] bg-white px-4 py-3 text-[13px] text-[#013523]"
        >
          Sign in to register for this event.
        </div>
      )}

      {/* Attendee running roster */}
      {ev.rsvp_enabled && (
        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5 space-y-3" data-testid="ce-roster-card">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[#013523] font-semibold flex items-center gap-1.5">
              <Users size={12} weight="bold" /> Attendees
            </div>
            <div className="text-[12px] font-semibold text-[#013523]" data-testid="ce-roster-count">
              {ev.attendee_count || 0} Registered
            </div>
          </div>
          <AttendeeRoster clubEventId={ev.id} refreshKey={rsvpTick} />
        </div>
      )}

      {/* Payment card — below the roster per spec. */}
      {ev.payment_enabled && (
        <PaymentCard
          ev={ev}
          viewer={user}
          onPaid={() => { load(); setRsvpTick((n) => n + 1); }}
          autoStartToken={autoStartToken}
        />
      )}

      {/* About This Event */}
      {(ev.description || showCta) && (
        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5 space-y-3" data-testid="ce-about">
          <div className="text-[11px] uppercase tracking-widest text-[#013523] font-semibold">
            About this event
          </div>
          {ev.description && (
            <div className="text-[14px] text-[#2a332c] leading-relaxed whitespace-pre-wrap" data-testid="ce-description">
              {ev.description}
            </div>
          )}
          {showCta && (
            <div className="pt-1">
              <a
                href={ev.cta_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ce-cta"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#013523] text-white text-sm font-medium hover:bg-[#012a1c] min-h-[44px]"
              >
                {ev.cta_label} <ArrowSquareOut size={12} />
              </a>
            </div>
          )}
        </div>
      )}

      <PostRegistrationPaymentPrompt
        open={payPromptOpen}
        onOpenChange={setPayPromptOpen}
        eventTypeLabel="Club Event"
        amountLabel={formatFee(ev.entry_fee_cents) || ''}
        paymentMethod={ev.payment_type}
        onPayNow={handlePayNow}
        onPayLater={handlePayLater}
      />
    </div>
  );
}
